import { useEffect, useState, useMemo } from 'react';
import { adminFetchContacts, adminUpdateContactRead, adminDeleteContact } from '../../lib/db';
import { notifyAdminContentUpdated } from '../../lib/site-settings';
import type { Contact } from '../../lib/types';
import { Mail, MailOpen, Trash2, Loader2, Search, ChevronLeft, ChevronRight, X } from 'lucide-react';

const PAGE_SIZE = 8;

export default function AdminContacts() {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [viewing, setViewing] = useState<Contact | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const data = await adminFetchContacts();
      setContacts(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return contacts;
    const q = search.toLowerCase();
    return contacts.filter(
      (c) => c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q) || c.subject.toLowerCase().includes(q)
    );
  }, [contacts, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  async function toggleRead(c: Contact) {
    const newRead = !c.read;
    setContacts(contacts.map((x) => (x.id === c.id ? { ...x, read: newRead } : x)));
    try {
      await adminUpdateContactRead(c.id, newRead);
      notifyAdminContentUpdated();
    } catch {
      setContacts(contacts.map((x) => (x.id === c.id ? { ...x, read: !newRead } : x)));
    }
  }

  async function handleDelete() {
    if (!deleteId) return;
    try {
      await adminDeleteContact(deleteId);
      setContacts(contacts.filter((c) => c.id !== deleteId));
      setDeleteId(null);
      notifyAdminContentUpdated();
    } catch {
      // ignore
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold text-stone-900">Messages</h2>
        <p className="text-stone-500 mt-1">Contact form submissions from your site.</p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
        <input
          type="text"
          placeholder="Search messages..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="input pl-10"
        />
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
          </div>
        ) : pageItems.length === 0 ? (
          <p className="text-center text-stone-500 py-16">No messages found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider hidden md:table-cell">Subject</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider hidden sm:table-cell">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider hidden lg:table-cell">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-stone-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {pageItems.map((c) => (
                  <tr
                    key={c.id}
                    className={`hover:bg-stone-50 transition-colors cursor-pointer ${!c.read ? 'bg-gold-50/40' : ''}`}
                    onClick={() => { setViewing(c); if (!c.read) toggleRead(c); }}
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-stone-900">{c.name}</p>
                      <p className="text-xs text-stone-500">{c.email}</p>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <p className="text-sm text-stone-700 line-clamp-1">{c.subject}</p>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      {c.read ? <span className="badge-gray">Read</span> : <span className="badge-gold">New</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-stone-500 hidden lg:table-cell">
                      {new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => toggleRead(c)} className="p-2 rounded-lg text-stone-500 hover:bg-brand-50 hover:text-brand-700 transition-colors" aria-label="Toggle read">
                          {c.read ? <MailOpen className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
                        </button>
                        <button onClick={() => setDeleteId(c.id)} className="p-2 rounded-lg text-stone-500 hover:bg-red-50 hover:text-red-600 transition-colors" aria-label="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-stone-500">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </p>
          <div className="flex gap-1">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="btn-ghost p-2 disabled:opacity-30">
              <ChevronLeft className="h-4 w-4" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button key={p} onClick={() => setPage(p)} className={`rounded-lg px-3 py-2 text-sm font-medium ${p === page ? 'bg-brand-600 text-white' : 'text-stone-600 hover:bg-stone-100'}`}>
                {p}
              </button>
            ))}
            <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="btn-ghost p-2 disabled:opacity-30">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* View modal */}
      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in" onClick={() => setViewing(null)}>
          <div className="card w-full max-w-lg max-h-[90vh] overflow-y-auto animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-stone-200">
              <h3 className="font-display text-lg font-semibold text-stone-900">Message</h3>
              <button onClick={() => setViewing(null)} className="p-2 rounded-lg hover:bg-stone-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-xs font-medium text-stone-500 uppercase">From</p>
                <p className="text-sm font-medium text-stone-900">{viewing.name}</p>
                <a href={`mailto:${viewing.email}`} className="text-sm text-brand-600 hover:underline">{viewing.email}</a>
              </div>
              <div>
                <p className="text-xs font-medium text-stone-500 uppercase">Subject</p>
                <p className="text-sm font-medium text-stone-900">{viewing.subject}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-stone-500 uppercase">Date</p>
                <p className="text-sm text-stone-700">{new Date(viewing.created_at).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-stone-500 uppercase mb-2">Message</p>
                <div className="rounded-lg bg-stone-50 p-4 text-sm text-stone-700 whitespace-pre-wrap">{viewing.message}</div>
              </div>
              <a href={`mailto:${viewing.email}`} className="btn-primary w-full">Reply via Email</a>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in" onClick={() => setDeleteId(null)}>
          <div className="card w-full max-w-sm p-6 animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600 mb-4">
              <Trash2 className="h-6 w-6" />
            </div>
            <h3 className="font-display text-lg font-semibold text-stone-900">Delete this message?</h3>
            <p className="mt-2 text-sm text-stone-600">This action cannot be undone.</p>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setDeleteId(null)} className="btn-outline flex-1">Cancel</button>
              <button onClick={handleDelete} className="btn-danger flex-1">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
